import { SimpleButtonComponent, TitleContentComponent, ToastFacade } from 'ngx-zcs-components';
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ChangeDetectorRef, NgZone, forwardRef } from '@angular/core';
import { CmdConnextService, CmdAzzurroHubService, generateUUID, GenericDeviceValidator, MapService, PlantPhasesInput, MonitoringModeInput, IntSensorInversionInput, AzzurroHubErrorCode, AzzurroHubDataType } from 'ngx-core-services';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatOptionModule } from '@angular/material/core';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { SimpleInputComponent, IconTextComponent } from 'ngx-zcs-components';

import { CreateFirebaseBlockService } from '../create-firebase/create-firebase-block.service';
import { ConnextConfiguration, AzzurroHubConfiguration, virtualConfiguration } from './configuration-section.models';
import { MapsFormComponent } from '../components/maps-form/maps-form.component';
import { PositionDetails } from '../components/maps-form/maps-form.models';
import { SystemDefinition } from '../../store';

const labels = {
  contractPowerDescription: 'Enter the contracted power in watts (W). This information can be found on your bill, usually on the first or second page, under the Contract Type section or in the Supply Data.',
  contractPowerPlaceholder: 'Example: 3000 W',
  contractPowerTitle: 'Contract power',
  externalPower: 'External power',
  externalPowerConsider: 'Consider',
  externalPowerDescription: 'With this option, you can choose whether or not to consider external power on your hybrid inverter in the calculation of total power.',
  externalPowerNotConsider: 'Do not consider',
  externalPowerSelectionPlaceholder: 'Example: Consider',
  locationDescription: 'Select the location of your plant on the map. It will allow you to have accurate monitoring.',
  locationTitle: 'Location',
  mapCoordinationPlaceholder: 'E.g. 5, Via di San Pietro, Rome',
  meterSignDescription: 'With this option you can change the sign of the meter.',
  meterSignInverted: 'Inverted',
  meterSignNormal: 'Normal',
  meterSignPlaceholder: 'Example: Normal',
  meterSignTitle: 'Meter sign (import/export)',
  phaseSelectPlaceholder: 'Example: Single phase',
  plantCreationConnextApply: 'Apply',
  plantCreationConnextDisclaimer: 'By applying the changes, if the plant has no owners, you will be assigned as the owner. Remember that for proper configuration, it is necessary to add the devices connected to your Connext.',
  plantCreationTitle: 'Plant creation',
  plantName: 'Plant name',
  plantNamePlaceholder: 'E.g. Giovanni House',
  plantTypeDescription: 'Choose the type of plant, Single-phase or Three-phase',
  plantTypeDisabled: 'This setting is blocked by the configuration of the installed devices.',
  plantTypeTitle: 'Plant type',
  sensorSignDescription: 'With this option, you can change the sensor sign.',
  sensorSignTitle: 'Sensor sign (import/export)',
  singlePhase: 'Single phase',
  threePhase: 'Three phase',
  updatingSystemConfiguration: 'Updating system configuration',
  monitoringModeTitle: 'Monitoring Mode',
  monitoringModeDescription: 'Select how the Azzurro Hub reads power and energy exchange data from your plant. This determines the monitoring source for power consumption and production.',
  monitoringModePlaceholder: 'Example: Inverter Master',
  monitoringModeInverterMaster: 'Inverter Master - Use inverter for PCC monitoring',
  monitoringModeMeterMaster: 'Meter Master - Use meter (modbus address 1) for PCC monitoring',
  monitoringModeInternalSensor: 'Internal Sensor - Use internal sensor for PCC monitoring (only 1 phase)',
  monitoringModePowerMagic: 'Power Magic Master - Use power magic for PCC monitoring',
  monitoringModeNone: 'No PCC monitoring',

  error: 'Error',
  unauthorized: 'Unauthorized',
  deviceDisconnected: 'Device disconnected',
  unknown: 'Unknown',
  timeout: 'Timeout exceeded',
  offlineDevice: 'Device is offline',
  offlineDeviceDescription: 'The device is currently offline. Please try again when it is online.',
  checkAgain: 'Check again',
};

@Component({
  standalone: true,
  imports: [
    CommonModule,
    forwardRef(() => IconTextComponent),
    forwardRef(() => MapsFormComponent),
    MatFormFieldModule,
    MatOptionModule,
    MatSelectModule,
    ReactiveFormsModule,
    forwardRef(() => SimpleButtonComponent),
    forwardRef(() => SimpleInputComponent),
    forwardRef(() => TitleContentComponent),
    TranslateModule,
  ],
  selector: 'customer-configuration-section',
  templateUrl: './configuration-section.component.html',
  styleUrls: ['./configuration-section.component.scss'],
})
export class ConfigurationSectionComponent implements OnInit, OnDestroy {
  @Input() virtualConfiguration!: virtualConfiguration;

  _connextConfiguration?: ConnextConfiguration;
  isConnextConfigurationChanged?: () => boolean;
  @Input() set connextConfiguration(value: ConnextConfiguration | undefined) {
    this._connextConfiguration = value;
    this.updateConnextConfiguration();
  }
  get connextConfiguration() {
    return this._connextConfiguration;
  }

  _azzurroHubConfiguration?: AzzurroHubConfiguration;
  isAzzurroHubConfigurationChanged?: () => boolean;
  @Input() set azzurroHubConfiguration(value: AzzurroHubConfiguration | undefined) {
    this._azzurroHubConfiguration = value;
    this.updateAzzurroHubConfiguration();
  }
  get azzurroHubConfiguration() {
    return this._azzurroHubConfiguration;
  }

  readonly _labels = labels;
  @Input() set labels(value: Partial<typeof this._labels>) {
    Object.assign(this._labels, value);
    this.updateMonitoringModeOptions();
  }
  get labels(): typeof this._labels {
    return this._labels;
  }

  @Input() isConnextLoggerSelected = false;
  @Input() isConnextLoggerOwner = false;
  @Input() connextSerial?: string | null;
  @Input() isConnextOffline = true;
  @Input() isAzzurroHubLoggerSelected = false;
  @Input() isAzzurroHubLoggerOwner = false;
  @Input() azzurroHubSerial?: string | null;
  @Input() isAzzurroHubOffline = false;
  @Input() isVerifiedMode = true;
  @Input() showMeterInversionInput: boolean | null = false;
  @Input() system?: SystemDefinition;

  _connextMeterConfig?: FormGroup<{ sign: FormControl<number | null> }>;
  isConnextMeterConfigChanged?: () => boolean;
  @Input() set connextMeterConfig(value: FormGroup<{ sign: FormControl<number | null> }> | undefined) {
    this._connextMeterConfig = value;
    const currentValue = value?.controls.sign.value;
    this.isConnextMeterConfigChanged = () => this._connextMeterConfig?.controls.sign.value !== currentValue;
  }
  get connextMeterConfig() {
    return this._connextMeterConfig;
  }

  _connextSensorConfig?: FormGroup<{ sign: FormControl<number | null> }>;
  isConnextSensorConfigChanged?: () => boolean;
  @Input() set connextSensorConfig(value: FormGroup<{ sign: FormControl<number | null> }> | undefined) {
    this._connextSensorConfig = value;
    const currentValue = value?.controls.sign.value;
    this.isConnextSensorConfigChanged = () => this._connextSensorConfig?.controls.sign.value !== currentValue;
  }
  get connextSensorConfig() {
    return this._connextSensorConfig;
  }

  _connextExternalConfig?: FormGroup<{ consider: FormControl<number | null> }>;
  isConnextExternalConfigChanged?: () => boolean;
  @Input() set connextExternalConfig(value: FormGroup<{ consider: FormControl<number | null> }> | undefined) {
    this._connextExternalConfig = value;
    const currentValue = value?.controls.consider.value;
    this.isConnextExternalConfigChanged = () => this._connextExternalConfig?.controls.consider.value !== currentValue;
  }
  get connextExternalConfig() {
    return this._connextExternalConfig;
  }

  @Output() applyConnextChanges = new EventEmitter<void>();
  @Output() applyAzzurroHubChanges = new EventEmitter<void>();
  @Output() forceAzzurroHubLoad = new EventEmitter<void>();

  readonly handleLocationControlChange = (input: PositionDetails, form: FormGroup, controlName: string) => {
    this.zone.run(() => {
      const control = form.get(controlName);
      if (control && input) {
        control.setValue(input);
        form.get(controlName)?.updateValueAndValidity();
        form.updateValueAndValidity();
      }

      this.changeDetectorRef.detectChanges();
    });
  };

  private connextConfigSubscription?: Subscription;
  private connextMeterConfigSubscription?: Subscription;
  private connextSensorConfigSubscription?: Subscription;
  private connextExternalConfigSubscription?: Subscription;
  private azzurroHubConfigSubscription?: Subscription;

  /** Order of this array is important for tests */
  readonly meterSignOptions =  [{
    label: labels.meterSignInverted,
    value: true
  }, {
    label: labels.meterSignNormal,
    value: false
  }];

  monitoringModeOptions: { label: string; value: MonitoringModeInput }[] = [];

  private updateMonitoringModeOptions(): void {
    this.monitoringModeOptions = [
      { label: this.labels.monitoringModeInverterMaster, value: MonitoringModeInput.invMaster },
      { label: this.labels.monitoringModeMeterMaster, value: MonitoringModeInput.meter },
      { label: this.labels.monitoringModeInternalSensor, value: MonitoringModeInput.intSensor },
      { label: this.labels.monitoringModePowerMagic, value: MonitoringModeInput.pwrMagic },
      { label: this.labels.monitoringModeNone, value: MonitoringModeInput.none },
    ];
  }

  /**
   * This flags are necessary to avoid reset default configuration on each configuration change
   * and to avoid check configuration on each configuration change (it's necessary only once)
   */
  @Input() needConnextDefaultConfiguration = true;
  needCheckConnextConfiguration = true;

  get controlCoordinates() {
    return this.canApplyConnextChanges
      ? this.connextConfiguration?.controls.coordinates
      : this.canApplyAzzurroHubChanges
        ? this.azzurroHubConfiguration?.controls.coordinates
        : this.virtualConfiguration.controls.coordinates;
  }

  get isControlCoordinatesEnabled() {
    return this.controlCoordinates?.status !== 'DISABLED';
  }

  get coordinates() {
    const coordinatesControl = this.controlCoordinates;
    const coordinates = coordinatesControl ? coordinatesControl.value : null;
    return coordinates ? {
      lat: Number(coordinates.latLng.lat().toPrecision(7)),
      lng: Number(coordinates.latLng.lng().toPrecision(7)),
    } : undefined;
  }

  get isConfigurationSectionInvalid() {
    return this.canApplyConnextChanges
      ? this.connextConfiguration?.invalid
      : this.canApplyAzzurroHubChanges
        ? this.azzurroHubConfiguration?.invalid
        : this.virtualConfiguration.invalid;
  }

  get isApplyConnextChangesEnabled() {
    return this.canApplyConnextChanges && (
      this.connextConfiguration?.controls.plantName.valid &&
      this.connextConfiguration?.controls.coordinates.valid &&
      this.connextConfiguration?.controls.plantPower.valid &&
      this.isPlantTypeValid
    ) && !this.connextConfiguration?.controls.applied.value;
  }

  get isApplyAzzurroHubChangesEnabled() {
    return this.canApplyAzzurroHubChanges && (
      this.azzurroHubConfiguration?.controls.plantName.valid &&
      this.azzurroHubConfiguration?.controls.coordinates.valid &&
      this.azzurroHubConfiguration?.controls.contractualPower.valid &&
      this.isAzzurroHubPlantTypeValid &&
      this.azzurroHubConfiguration?.controls.monitoringMode.valid
    ) && !this.azzurroHubConfiguration?.controls.applied.value;
  }

  get isPlantTypeValid() {
    const plantType = this.connextConfiguration?.controls.plantType.value;
    return plantType === 0 || plantType === 1;
  }

  get isAzzurroHubPlantTypeValid() {
    const plantType = this.azzurroHubConfiguration?.controls.plantType.value;
    return plantType === 0 || plantType === 1;
  }

  get isPlantTypeDisabled() {
    return this.connextConfiguration?.controls.applied.status === 'DISABLED';
  }

  get isAzzurroHubPlantTypeDisabled() {
    return this.azzurroHubConfiguration?.controls.applied.status === 'DISABLED';
  }

  get plantConfiguration() {
    return this.canApplyConnextChanges
      ? this.connextConfiguration
      : this.canApplyAzzurroHubChanges
        ? this.azzurroHubConfiguration
        : this.virtualConfiguration;
  }

  get canApplyConnextChanges() {
    return this.isConnextLoggerSelected && this.isConnextLoggerOwner && !this.isConnextOffline;
  }

  get canApplyAzzurroHubChanges() {
    return this.isAzzurroHubLoggerSelected && (this.system === undefined || this.isAzzurroHubLoggerOwner) && !this.isAzzurroHubOffline;
  }

  constructor(
    private readonly changeDetectorRef: ChangeDetectorRef,
    private readonly cmdConnextService: CmdConnextService,
    private readonly cmdAzzurroHubService: CmdAzzurroHubService,
    private readonly genericDeviceValidator: GenericDeviceValidator,
    private readonly mapService: MapService,
    private readonly toastFacade: ToastFacade,
    private readonly zone: NgZone,
    readonly createFirebaseBlockService: CreateFirebaseBlockService,
  ) {
    this.configConnext = this.configConnext.bind(this);
    this.updateConnextConfiguration = this.updateConnextConfiguration.bind(this);
    this.configAzzurroHub = this.configAzzurroHub.bind(this);
    this.updateAzzurroHubConfiguration = this.updateAzzurroHubConfiguration.bind(this);
    this.checkAzzurroHubConnection = this.checkAzzurroHubConnection.bind(this);
  }

  ngOnInit(): void {
    this.updateMonitoringModeOptions();
    this.connextConfigSubscription = this.connextConfiguration?.valueChanges.subscribe((data) => {
      if (this.connextConfiguration?.controls.applied.value === false) {
        return;
      }

      if (this.isConnextConfigurationChanged?.()) {
        this.connextConfiguration?.controls.applied.setValue(false);
      }
    });

    this.connextMeterConfigSubscription = this.connextMeterConfig?.valueChanges.subscribe(() => {
      if (this.connextConfiguration?.controls.applied.value === false) {
        return;
      }

      if (this.isConnextMeterConfigChanged?.()) {
        this.connextConfiguration?.controls.applied.setValue(false);
      }
    });

    this.connextSensorConfigSubscription = this.connextSensorConfig?.valueChanges.subscribe(() => {
      if (this.connextConfiguration?.controls.applied.value === false) {
        return;
      }

      if (this.isConnextSensorConfigChanged?.()) {
        this.connextConfiguration?.controls.applied.setValue(false);
      }
    });

    this.connextExternalConfigSubscription = this.connextExternalConfig?.valueChanges.subscribe(() => {
      if (this.connextConfiguration?.controls.applied.value === false) {
        return;
      }

      if (this.isConnextExternalConfigChanged?.()) {
        this.connextConfiguration?.controls.applied.setValue(false);
      }
    });

    this.azzurroHubConfigSubscription = this.azzurroHubConfiguration?.valueChanges.subscribe((data) => {
      if (this.azzurroHubConfiguration?.controls.applied.value === false) {
        return;
      }

      if (this.isAzzurroHubConfigurationChanged?.()) {
        this.azzurroHubConfiguration?.controls.applied.setValue(false);
      }
    });
  }

  ngOnDestroy(): void {
    this.connextConfigSubscription?.unsubscribe();
    this.connextMeterConfigSubscription?.unsubscribe();
    this.connextSensorConfigSubscription?.unsubscribe();
    this.connextExternalConfigSubscription?.unsubscribe();
    this.azzurroHubConfigSubscription?.unsubscribe();
  }

  async configConnext() {
    const noAutoOwner = this.isVerifiedMode;
    if (!this.connextSerial) {
      return;
    }

    if (this.isConnextOffline) {
      return this.toastFacade.present('Device is offline, please try again when it is online.', 'Error', {
        severity: 'error',
      });
    }

    const uid = await this.genericDeviceValidator.getConnextUid(this.connextSerial);
    if (!uid) {
      return;
    }

    if (this.needConnextDefaultConfiguration) {
      const defaultConfig = await this.cmdConnextService.sendCommandStrategy(
        uid,
        'reg i UA',
        this.labels.updatingSystemConfiguration,
        this.isVerifiedMode,
        false,
        noAutoOwner,
        this.system?.uid,
      );

      if (defaultConfig?.error) {
        return;
      }

      this.needConnextDefaultConfiguration = false;
    }

    if (!this.isConnextConfigurationChanged?.()) {
      return await this.applyAdditionalConnextConfiguration();
    }

    const coordinates = this.connextConfiguration?.controls.coordinates.value;
    if (!coordinates) {
      return;
    }

    if (!coordinates.timezone) {
      const _timezone = await this.mapService.getTz(coordinates.latLng);
      if (!_timezone) {
        return;
      }
      coordinates.timezone = _timezone;
    }

    const plantType = Number(this.connextConfiguration?.controls.plantType.value);
    const plantPower = Math.floor(Number(this.connextConfiguration?.controls.plantPower.value) / 100);
    let timezone = Math.floor(Number(coordinates?.timezone?.gmtOffset) / 3600);
    timezone = coordinates?.timezone?.dst === '1' ? timezone - 1 : timezone;
    timezone *= 4;
    const locationLat = `C${coordinates?.latLng.lat().toPrecision(7)}`;
    const locationLong = `${coordinates?.latLng.lng().toPrecision(7)}`;

    const cmd = `set plant ${plantType} ${plantPower} ${timezone} ${locationLat} ${locationLong}`;
    const plantName = this.connextConfiguration?.controls.plantName.value;

    if (!plantName) {
      return;
    }

    if( !this.isVerifiedMode ) {
      const plantUid = generateUUID();
      this.connextConfiguration?.controls.plantUid.setValue(plantUid || '');

      const plantResponse = await this.createFirebaseBlockService.createFirebasePlant(plantName, {
        connextId: uid,
        coordinates,
        uid: plantUid,
        waitBeforeRedirect: 1,
      })();

      if (plantResponse?.error) {
        return;
      }
    }

    const cmdResponse = await this.cmdConnextService.sendCommandStrategy(
      uid,
      cmd,
      this.labels.updatingSystemConfiguration,
      this.isVerifiedMode,
      this.needCheckConnextConfiguration,
      noAutoOwner,
      this.system?.uid,
    );

    if (cmdResponse?.error) {
      return;
    }

    this.needCheckConnextConfiguration = false;

    setTimeout(() => {
      this.updateConnextConfiguration();
      this.connextConfiguration?.controls.applied.setValue(true);
      this.connextConfiguration?.controls.plantName.setValue(plantName);
      this.connextConfiguration?.controls.coordinates.setValue(coordinates);
      this.applyConnextChanges.emit();
      this.changeDetectorRef.detectChanges();
    }, 500);
  }

  updateConnextConfiguration() {
    this.isConnextConfigurationChanged = (() => {
      const plantNameCurrentValue = this.connextConfiguration?.controls.plantName.value;
      const coordinatesLatCurrentValue = this.connextConfiguration?.controls.coordinates.value?.latLng.lat().toPrecision(7);
      const coordinatesLngCurrentValue = this.connextConfiguration?.controls.coordinates.value?.latLng.lng().toPrecision(7);
      const timezoneCurrentValue = this.connextConfiguration?.controls.coordinates.value?.timezone;
      const plantPowerCurrentValue = this.connextConfiguration?.controls.plantPower.value;
      const plantTypeCurrentValue = this.connextConfiguration?.controls.plantType.value;

      return () => this.connextConfiguration?.controls.plantName.value !== plantNameCurrentValue ||
        this.connextConfiguration?.controls.plantPower.value !== plantPowerCurrentValue ||
        this.connextConfiguration?.controls.plantType.value !== plantTypeCurrentValue ||
        this.connextConfiguration?.controls.coordinates.value?.latLng.lat().toPrecision(7) !== coordinatesLatCurrentValue ||
        this.connextConfiguration?.controls.coordinates.value?.latLng.lng().toPrecision(7) !== coordinatesLngCurrentValue ||
        (
          this.connextConfiguration?.controls.coordinates.value?.timezone !== null &&
          this.connextConfiguration?.controls.coordinates.value?.timezone !== timezoneCurrentValue
        );
    })();
  }

  async applyAdditionalConnextConfiguration() {
    const noAutoOwner = this.isVerifiedMode;
    if (!this.connextSerial) {
      return;
    }

    const uid = await this.genericDeviceValidator.getConnextUid(this.connextSerial);
    if (!uid) {
      return;
    }

    const plantUid = this.connextConfiguration?.controls.plantUid.value;
    if (!plantUid) {
      return;
    }

    if (this.connextMeterConfig && this.isConnextMeterConfigChanged?.()) {
      const sign = this.connextMeterConfig.controls.sign.value;
      const cmd = `set sensor mt ${sign}`;
      const response = await this.cmdConnextService.sendCommandStrategy(
        uid,
        cmd,
        this.labels.updatingSystemConfiguration,
        this.isVerifiedMode,
        false,
        noAutoOwner,
        this.system?.uid,
      );

      if (response?.error) {
        return;
      }
      this.isConnextMeterConfigChanged = (() => {
        const currentValue = this.connextMeterConfig?.controls.sign.value;
        return () => this.connextMeterConfig?.controls.sign.value !== currentValue;
      })();
    }

    if (this.connextSensorConfig && this.isConnextSensorConfigChanged?.()) {
      const sign = this.connextSensorConfig.controls.sign.value;
      const cmd = `set sensor ta ${sign}`;
      const response = await this.cmdConnextService.sendCommandStrategy(
        uid,
        cmd,
        this.labels.updatingSystemConfiguration,
        this.isVerifiedMode,
        false,
        noAutoOwner,
        this.system?.uid,
      );

      if (response?.error) {
        return;
      }
      this.isConnextSensorConfigChanged = (() => {
        const currentValue = this.connextSensorConfig?.controls.sign.value;
        return () => this.connextSensorConfig?.controls.sign.value !== currentValue;
      })();
    }

    if (this.connextExternalConfig && this.isConnextExternalConfigChanged?.()) {
      const consider = this.connextExternalConfig.controls.consider.value;
      const cmd = `set pext ${consider}`;
      const response = await this.cmdConnextService.sendCommandStrategy(
        uid,
        cmd,
        this.labels.updatingSystemConfiguration,
        this.isVerifiedMode,
        false,
        noAutoOwner,
        this.system?.uid,
      );

      if (response?.error) {
        return;
      }
      this.isConnextExternalConfigChanged = (() => {
        const currentValue = this.connextExternalConfig?.controls.consider.value;
        return () => this.connextExternalConfig?.controls.consider.value !== currentValue;
      })();
    }

    this.connextConfiguration?.controls.applied.setValue(true);

    this.applyConnextChanges.emit();
  }

  async configAzzurroHub() {
    const plantName = this.azzurroHubConfiguration?.controls.plantName.value!;
    const coordinates = this.azzurroHubConfiguration?.controls.coordinates.value!;
    const contractualPower = this.azzurroHubConfiguration?.controls.contractualPower.value!;
    const plantType = this.azzurroHubConfiguration?.controls.plantType.value!;
    const monitoringMode = this.azzurroHubConfiguration?.controls.monitoringMode.value;

    const serial = this.azzurroHubSerial;
    if (!serial) {
      return;
    }

    if (!coordinates?.timezone) {
      const _timezone = await this.mapService.getTz(coordinates.latLng);
      if (!_timezone) {
        return;
      }
      coordinates.timezone = _timezone;
    }

    try {
      // Configurazione completa Azzurro Hub usando il nuovo servizio
      const configResponse = await this.cmdAzzurroHubService.configureAzzurroHubStrategy(
        serial,
        {
          timezone: coordinates.timezone.zoneName || 'Europe/Rome',
          country: coordinates.timezone.countryCode,
          contractualPower: Number(contractualPower), // Solo maxContractualPwr viene modificato
          plantType: plantType === 1 ? PlantPhasesInput.threePhase : PlantPhasesInput.monoPhase,
          monitoringMode: monitoringMode ?? MonitoringModeInput.invMaster,
          sensorInversion: IntSensorInversionInput.none, // Default nessuna inversione
        },
        this.labels.updatingSystemConfiguration
      );

      this.forceAzzurroHubLoad.emit();

      if (configResponse?.error) {

        let errorMessage = this.labels.unknown;
        switch (configResponse.error.code) {
          case AzzurroHubErrorCode.Unauthorized:
            errorMessage = this.labels.unauthorized;
            break;
          case AzzurroHubErrorCode.DeviceNotConnected:
            errorMessage = this.labels.deviceDisconnected;
            break;
          case AzzurroHubErrorCode.Timeout:
            errorMessage = this.labels.timeout;
            break;
          default:
            errorMessage = configResponse.error.message || this.labels.unknown;
            break;
        }

        this.toastFacade.present(`${errorMessage}`, this.labels.error, {
          severity: 'error',
        });

        return;
      }

      // Successo - aggiorna stato
      setTimeout(() => {
        this.updateAzzurroHubConfiguration();
        this.azzurroHubConfiguration?.controls.applied.setValue(true);
        this.azzurroHubConfiguration?.controls.plantName.setValue(plantName);
        this.azzurroHubConfiguration?.controls.coordinates.setValue(coordinates);
        this.applyAzzurroHubChanges.emit();
        this.changeDetectorRef.detectChanges();
      }, 500);

    } catch (error: any) {
      console.error('Errore configurazione Azzurro Hub:', error);
      this.toastFacade.present('Errore durante la configurazione', 'Error', {
        severity: 'error',
      });
    }
  }

  checkAzzurroHubConnection() {
    this.forceAzzurroHubLoad.emit();
  }

  updateAzzurroHubConfiguration() {
    this.isAzzurroHubConfigurationChanged = (() => {
      const plantNameCurrentValue = this.azzurroHubConfiguration?.controls.plantName.value;
      const coordinatesLatCurrentValue = this.azzurroHubConfiguration?.controls.coordinates.value?.latLng.lat().toPrecision(7);
      const coordinatesLngCurrentValue = this.azzurroHubConfiguration?.controls.coordinates.value?.latLng.lng().toPrecision(7);
      const timezoneCurrentValue = this.azzurroHubConfiguration?.controls.coordinates.value?.timezone;
      const contractualPowerCurrentValue = this.azzurroHubConfiguration?.controls.contractualPower.value;
      const plantTypeCurrentValue = this.azzurroHubConfiguration?.controls.plantType.value;
      const monitoringModeCurrentValue = this.azzurroHubConfiguration?.controls.monitoringMode.value;

      return () => this.azzurroHubConfiguration?.controls.plantName.value !== plantNameCurrentValue ||
        this.azzurroHubConfiguration?.controls.contractualPower.value !== contractualPowerCurrentValue ||
        this.azzurroHubConfiguration?.controls.plantType.value !== plantTypeCurrentValue ||
        this.azzurroHubConfiguration?.controls.monitoringMode.value !== monitoringModeCurrentValue ||
        this.azzurroHubConfiguration?.controls.coordinates.value?.latLng.lat().toPrecision(7) !== coordinatesLatCurrentValue ||
        this.azzurroHubConfiguration?.controls.coordinates.value?.latLng.lng().toPrecision(7) !== coordinatesLngCurrentValue ||
        (
          this.azzurroHubConfiguration?.controls.coordinates.value?.timezone !== null &&
          this.azzurroHubConfiguration?.controls.coordinates.value?.timezone !== timezoneCurrentValue
        );
    })();
  }
}
